import React from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  TextField,
  Divider,
  IconButton,
  Tooltip,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';

import Dropzone from 'react-dropzone';
import QRCode from 'qrcode';

const FONT_OPTIONS = [
  'Arial','Verdana','Tahoma','Georgia','Courier New','Times New Roman','Trebuchet MS',
  'Helvetica','Impact','Comic Sans MS','Lucida Console','Palatino Linotype','Segoe UI',
  'Gill Sans','Candara','Garamond','Bookman','Consolas','Courier','Optima','Franklin Gothic Medium'
];

const ASPECTS = [
  { label: '16:9', value: 16/9 },
  { label: '9:16', value: 9/16 },
  { label: '4:3',  value: 4/3  },
  { label: '5:4',  value: 5/4  },
  { label: '4:5',  value: 4/5  },
  { label: '3:2',  value: 3/2  },
  { label: '2:3',  value: 2/3  },
  { label: '3:4',  value: 3/4  },
  { label: '1:1',  value: 1    },
];

const FIT_MODES = [
  { label: 'Cover (fills, may crop)', value: 'cover' },
  { label: 'Contain (no crop, letterbox)', value: 'contain' },
];

const DEFAULT_QR = 'https://www.elevator.tv';

const newText = (id, x, y) => ({
  id,
  text: 'text',
  x, y,
  align: 'left',
  fontFamily: 'Arial',
  fontSize: 48,
  lineHeightMult: 1.15,
  letterSpacing: 0,
  color: '#ffffff',
  outlineColor: '#000000',
  outlineWidth: 4,
  backdrop: false,
  backdropColor: '#000000',
  backdropOpacity: 0.4,
  wrapWFrac: 0.9,
});

function hexToRgba(hex, alpha = 1) {
  let h = (hex || '#000').replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const num = parseInt(h, 16);
  const r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default class ImageEditor extends React.Component {
  constructor(props) {
    super(props);

    const initialAspect = typeof props.contentRatio === 'number' ? props.contentRatio : 3/4;

    this.state = {
      imageSrc: null,
      imageLoading: false,

      aspectRatio: initialAspect,
      fitMode: 'cover',

      texts: [],
      selectedId: null,

      hoverId: null,

      // editing
      editingId: null,
      typingActive: false,
      caretOn: true,

      showQrPreview: true,

      // guides (snap)
      guideV: null, // x
      guideH: null, // y

      renderMeta: { baseW: 1000, baseH: Math.round(1000 / initialAspect) },

      // one-time hint
      showHint: true,
    };

    this.canvasRef = React.createRef();
    this.hiddenInputRef = React.createRef();

    this.caretTimer = null;

    // render scheduling
    this._raf = null;
    this._dirty = false;

    // drag bookkeeping (use refs to avoid setState spam)
    this.drag = {
      active: false,
      pointerId: null,
      started: false,
      startX: 0,
      startY: 0,
      lastX: 0,
      lastY: 0,
      startText: null, // snapshot of selected text
      shift: false,
      alt: false,
    };

    // undo/redo
    this.history = [];
    this.future = [];

    // QR cache
    this.qrCache = { key: '', img: null };

    // last pointer position for "add text" convenience
    this.lastPointer = { x: 500, y: 500 };
  }

  componentDidMount() {
    window.addEventListener('keydown', this.onKeyDownWindow);
  }
  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKeyDownWindow);
    this.stopCaret();
    if (this._raf) cancelAnimationFrame(this._raf);
  }

  // -----------------------------
  // History (Undo / Redo)
  // -----------------------------
  snapshotState() {
    const { imageSrc, aspectRatio, fitMode, texts, selectedId, showQrPreview } = this.state;
    return {
      imageSrc,
      aspectRatio,
      fitMode,
      texts: JSON.parse(JSON.stringify(texts)),
      selectedId,
      showQrPreview,
    };
  }

  restoreSnapshot(snap) {
    this.setState({
      imageSrc: snap.imageSrc,
      aspectRatio: snap.aspectRatio,
      fitMode: snap.fitMode,
      texts: snap.texts,
      selectedId: snap.selectedId,
      showQrPreview: snap.showQrPreview ?? true,
      editingId: null,
      typingActive: false,
      guideV: null,
      guideH: null,
    }, this.markDirty);
  }

  pushHistory = () => {
    this.history.push(this.snapshotState());
    if (this.history.length > 80) this.history.shift();
    this.future = [];
  };

  undo = () => {
    if (!this.history.length) return;
    const current = this.snapshotState();
    const prev = this.history.pop();
    this.future.push(current);
    this.restoreSnapshot(prev);
  };

  redo = () => {
    if (!this.future.length) return;
    const current = this.snapshotState();
    const next = this.future.pop();
    this.history.push(current);
    this.restoreSnapshot(next);
  };

  // -----------------------------
  // Rendering scheduling
  // -----------------------------
  markDirty = () => {
    this._dirty = true;
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = null;
      if (this._dirty) {
        this._dirty = false;
        this.renderCanvas();
      }
    });
  };

  // -----------------------------
  // Caret blink
  // -----------------------------
  startCaret() {
    this.stopCaret();
    this.caretTimer = setInterval(() => {
      this.setState(s => ({ caretOn: !s.caretOn }), this.markDirty);
    }, 500);
  }
  stopCaret() {
    if (this.caretTimer) clearInterval(this.caretTimer);
    this.caretTimer = null;
    this.setState({ caretOn: true });
  }

  // -----------------------------
  // File drop
  // -----------------------------
  onDrop = (accepted) => {
    const f = accepted?.[0];
    if (!f) return;

    this.pushHistory();

    this.setState({ imageLoading: true, imageSrc: null }, () => {
      const fr = new FileReader();
      fr.onload = () => {
        this.setState({ imageSrc: fr.result, imageLoading: false, showHint: true }, this.markDirty);
      };
      fr.readAsDataURL(f);
    });
  };

  // -----------------------------
  // Measure / Wrap
  // -----------------------------
  measureLineWidth(ctx, text, spacing) {
    if (!text) return 0;
    if (!spacing) return ctx.measureText(text).width;
    let w = 0;
    for (const ch of text) w += ctx.measureText(ch).width;
    w += spacing * Math.max(0, text.length - 1);
    return w;
  }

  wrapLines(ctx, text, maxWidth, spacing) {
    const paras = (text || '').split('\n');
    const lines = [];
    for (const p of paras) {
      const words = p.split(' ');
      let line = '';
      for (let i = 0; i < words.length; i++) {
        const test = line + words[i] + ' ';
        const w = this.measureLineWidth(ctx, test, spacing);
        if (w > maxWidth && i > 0) {
          lines.push(line.trimEnd());
          line = words[i] + ' ';
        } else {
          line = test;
        }
      }
      lines.push(line.trimEnd());
    }
    return lines.length ? lines : [''];
  }

  // -----------------------------
  // Drawing helpers
  // -----------------------------
  fitAndDrawImage(ctx, img, W, H, mode) {
    const iw = img.width, ih = img.height, ir = iw / ih, tr = W / H;
    if (mode === 'cover') {
      let sx = 0, sy = 0, sw = iw, sh = ih;
      if (ir > tr) { sw = ih * tr; sx = (iw - sw) / 2; }
      else { sh = iw / tr; sy = (ih - sh) / 2; }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, W, H);
    } else {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, W, H);

      let dw = W, dh = H, dx = 0, dy = 0;
      if (ir > tr) { dh = W / ir; dy = (H - dh) / 2; }
      else { dw = H * ir; dx = (W - dw) / 2; }
      ctx.drawImage(img, 0, 0, iw, ih, dx, dy, dw, dh);
    }
  }

  computeTextMetrics(ctx, T, baseW) {
    const {
      x, y, align, fontFamily, fontSize, lineHeightMult,
      letterSpacing, wrapWFrac, text
    } = T;

    const lineHeight = fontSize * lineHeightMult;
    const maxW = Math.max(10, baseW * wrapWFrac);

    ctx.font = `bold ${fontSize}px ${fontFamily}`;
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = align;

    const lines = this.wrapLines(ctx, text, maxW, letterSpacing);
    const widest = Math.max(...lines.map(l => this.measureLineWidth(ctx, l, letterSpacing)));
    const totalH = Math.max(lineHeight, lines.length * lineHeight);

    let sx = x, sw = widest;
    if (align === 'center') sx = x - widest / 2;
    if (align === 'right')  sx = x - widest;
    const sy = y - fontSize;
    const sh = totalH;

    return { lines, lineHeight, maxW, widest, totalH, box: { x: sx, y: sy, w: sw, h: sh } };
  }

  drawText(ctx, T, baseW) {
    const {
      x, y, align, fontFamily, fontSize, lineHeightMult,
      letterSpacing, color, outlineColor, outlineWidth,
      backdrop, backdropColor, backdropOpacity, wrapWFrac, text
    } = T;

    const m = this.computeTextMetrics(ctx, T, baseW);
    const { lines, lineHeight, widest, totalH } = m;

    // backdrop
    if (backdrop) {
      const pad = Math.max(8, fontSize * 0.3);
      let bx = x, bw = widest;
      if (align === 'center') bx = x - widest / 2;
      if (align === 'right')  bx = x - widest;
      bx -= pad;
      const by = y - fontSize - pad;
      const bh = totalH + pad * 2;
      ctx.fillStyle = hexToRgba(backdropColor, backdropOpacity);
      ctx.fillRect(bx, by, bw + pad * 2, bh);
    }

    // outline + fill + subtle shadow
    ctx.lineJoin = 'round';
    ctx.miterLimit = 2;
    ctx.lineWidth = outlineWidth;
    ctx.strokeStyle = outlineColor;
    ctx.fillStyle = color;
    ctx.shadowColor = 'rgba(0,0,0,0.55)';
    ctx.shadowBlur = 6;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;

    lines.forEach((line, i) => {
      const baseWidth = this.measureLineWidth(ctx, line, letterSpacing);
      let cursorX = x;
      if (align === 'center') cursorX -= baseWidth / 2;
      if (align === 'right')  cursorX -= baseWidth;
      const lineY = y + i * lineHeight;

      if (letterSpacing) {
        for (const ch of line) {
          ctx.strokeText(ch, cursorX, lineY);
          ctx.fillText(ch, cursorX, lineY);
          cursorX += ctx.measureText(ch).width + letterSpacing;
        }
      } else {
        ctx.strokeText(line, x, lineY);
        ctx.fillText(line, x, lineY);
      }
    });

    return m;
  }

  drawSelection(ctx, T, baseW, metrics, isEditing) {
    const { align, x, wrapWFrac } = T;
    const { box, maxW } = metrics;

    ctx.save();
    ctx.shadowColor = 'transparent';

    // main bounding box
    ctx.setLineDash(isEditing ? [] : [6, 4]);
    ctx.lineWidth = isEditing ? 2 : 1.5;
    ctx.strokeStyle = isEditing ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.85)';
    ctx.strokeRect(box.x - 6, box.y - 6, box.w + 12, box.h + 12);

    // handles (visual affordance)
    const hx = box.x - 6, hy = box.y - 6, hw = box.w + 12, hh = box.h + 12;
    const r = 4;
    const pts = [
      [hx, hy], [hx + hw, hy],
      [hx, hy + hh], [hx + hw, hy + hh],
    ];
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    pts.forEach(([px, py]) => {
      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.fill();
    });

    // wrap-width guide bar
    const wx = (align === 'left') ? x : (align === 'center') ? (x - maxW/2) : (x - maxW);
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.setLineDash([3, 3]);
    ctx.strokeRect(wx, box.y - 16, maxW, 10);

    // anchor dot
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.beginPath();
    ctx.arc(x, box.y + 6, 2.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  drawGuides(ctx, baseW, baseH) {
    const { guideV, guideH } = this.state;
    if (guideV == null && guideH == null) return;

    ctx.save();
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);

    if (guideV != null) {
      ctx.beginPath();
      ctx.moveTo(guideV, 0);
      ctx.lineTo(guideV, baseH);
      ctx.stroke();
    }
    if (guideH != null) {
      ctx.beginPath();
      ctx.moveTo(0, guideH);
      ctx.lineTo(baseW, guideH);
      ctx.stroke();
    }
    ctx.restore();
  }

  // -----------------------------
  // QR overlay
  // -----------------------------
  async ensureQrImage(size, value) {
    const val = value || this.props.qrValue || DEFAULT_QR;
    const key = `${val}::${size}`;
    if (this.qrCache.key === key && this.qrCache.img) return this.qrCache.img;

    const dataUrl = await QRCode.toDataURL(val, { width: size, margin: 0 });
    const img = await new Promise((resolve) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.src = dataUrl;
    });
    this.qrCache = { key, img };
    return img;
  }

  async drawQrOverlay(ctx, baseW, baseH) {
    const size = Math.max(8, Math.round(baseW * 0.25));
    const img = await this.ensureQrImage(size, this.props.qrValue);
    const x = baseW - size - 10;
    const y = baseH - size - 10;

    ctx.save();
    ctx.shadowColor = 'transparent';
    ctx.drawImage(img, x, y, size, size);
    ctx.restore();
  }

  // -----------------------------
  // Main render
  // -----------------------------
  async renderCanvas() {
    const { imageSrc, aspectRatio, fitMode } = this.state;
    const canvas = this.canvasRef.current;
    if (!canvas || !imageSrc) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const img = new Image();
    await new Promise((res) => { img.onload = res; img.src = imageSrc; });

    const baseW = 1000;
    const baseH = Math.round(baseW / aspectRatio);

    // crisp render with device scale
    const scale = 2;
    canvas.width = baseW * scale;
    canvas.height = baseH * scale;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.clearRect(0, 0, baseW, baseH);

    this.fitAndDrawImage(ctx, img, baseW, baseH, fitMode);

    // draw guides first (behind selection / text is fine either way)
    this.drawGuides(ctx, baseW, baseH);

    // draw texts
    const { selectedId, editingId, typingActive, caretOn } = this.state;

    this.state.texts.forEach(t => {
      const metrics = this.drawText(ctx, t, baseW);

      const isSel = t.id === selectedId;
      const isEdit = t.id === editingId && typingActive;

      if (isSel) this.drawSelection(ctx, t, baseW, metrics, isEdit);

      // caret (simple, end of last line)
      if (isEdit && caretOn) {
        const last = metrics.lines[metrics.lines.length - 1] || '';
        const w = this.measureLineWidth(ctx, last, t.letterSpacing);

        let cx = t.x;
        if (t.align === 'left')   cx = t.x + w;
        if (t.align === 'center') cx = t.x + w / 2;
        if (t.align === 'right')  cx = t.x;

        const cy = t.y + (metrics.lines.length - 1) * metrics.lineHeight;

        ctx.save();
        ctx.shadowColor = 'transparent';
        ctx.strokeStyle = t.color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(cx, cy - metrics.lineHeight * 0.9);
        ctx.lineTo(cx, cy + metrics.lineHeight * 0.15);
        ctx.stroke();
        ctx.restore();
      }
    });

    // snapshot BEFORE QR (for parent hook)
    const preQrDataUrl = (!this.state.typingActive && this.props.onChange)
      ? canvas.toDataURL('image/png')
      : null;

    // QR on top (preview only)
    if (this.state.showQrPreview) {
      await this.drawQrOverlay(ctx, baseW, baseH);
    }

    // remember dims
    if (this.state.renderMeta.baseW !== baseW || this.state.renderMeta.baseH !== baseH) {
      this.setState({ renderMeta: { baseW, baseH } });
    }

    // notify parent with image WITHOUT QR
    if (preQrDataUrl && this.props.onChange) {
      const contentRatio = baseW / baseH;
      try { this.props.onChange(preQrDataUrl, contentRatio); } catch {}
    }
  }

  // -----------------------------
  // Hit test + coordinate conversion
  // -----------------------------
  hitText(baseX, baseY) {
    const canvas = this.canvasRef.current;
    if (!canvas) return null;

    const ctx = canvas.getContext('2d');
    const baseW = this.state.renderMeta.baseW;

    for (let i = this.state.texts.length - 1; i >= 0; i--) {
      const T = this.state.texts[i];
      ctx.font = `bold ${T.fontSize}px ${T.fontFamily}`;

      const m = this.computeTextMetrics(ctx, T, baseW);
      const b = m.box;

      // padding helps selection on touch
      const pad = 10;
      if (baseX >= b.x - pad && baseX <= b.x + b.w + pad && baseY >= b.y - pad && baseY <= b.y + b.h + pad) {
        return T.id;
      }
    }
    return null;
  }

  canvasToBaseFromClient(clientX, clientY) {
    const canvas = this.canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const xCss = clientX - rect.left;
    const yCss = clientY - rect.top;
    const { baseW, baseH } = this.state.renderMeta;
    return { x: (xCss / rect.width) * baseW, y: (yCss / rect.height) * baseH };
  }

  // -----------------------------
  // Editing (hidden input) + inspector text field
  // -----------------------------
  beginEditing = (id) => {
    const ta = this.hiddenInputRef.current;
    const T = this.state.texts.find(t => t.id === id);
    if (!ta || !T) return;

    ta.value = T.text || '';
    ta.focus();
    ta.setSelectionRange(ta.value.length, ta.value.length);

    this.setState({ editingId: id, typingActive: true }, () => {
      this.startCaret();
      this.markDirty();
    });
  };

  commitEditing = () => {
    const ta = this.hiddenInputRef.current;
    if (ta) ta.blur();
  };

  onHiddenChange = (e) => {
    const id = this.state.editingId;
    if (!id) return;

    const val = e.target.value;
    this.setState(s => ({
      texts: s.texts.map(t => t.id === id ? { ...t, text: val } : t),
    }), this.markDirty);
  };

  onHiddenKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.blur();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  onHiddenBlur = () => {
    const id = this.state.editingId;
    if (id) {
      const T = this.state.texts.find(t => t.id === id);
      if (T && !(T.text || '').trim()) {
        // remove empty text box
        this.pushHistory();
        this.setState(s => ({
          texts: s.texts.filter(t => t.id !== id),
          selectedId: null,
          editingId: null,
          typingActive: false,
        }), this.markDirty);
        return;
      }
    }

    this.stopCaret();
    this.setState({ editingId: null, typingActive: false }, this.markDirty);
  };

  // -----------------------------
  // UX: Cursor + pointer events
  // -----------------------------
  getCanvasCursor() {
    const { hoverId, selectedId, typingActive } = this.state;
    if (typingActive) return 'text';
    if (hoverId) return 'move';
    if (selectedId) return 'default';
    return 'crosshair';
  }

  onPointerDownCanvas = (e) => {
    const canvas = this.canvasRef.current;
    if (!canvas) return;

    canvas.setPointerCapture?.(e.pointerId);

    const { x, y } = this.canvasToBaseFromClient(e.clientX, e.clientY);
    this.lastPointer = { x, y };

    const hitId = this.hitText(x, y);

    // start drag candidate if hitting selected or any text
    this.drag.active = true;
    this.drag.pointerId = e.pointerId;
    this.drag.started = false;
    this.drag.startX = x;
    this.drag.startY = y;
    this.drag.lastX = x;
    this.drag.lastY = y;
    this.drag.shift = e.shiftKey;
    this.drag.alt = e.altKey;

    if (hitId) {
      const T = this.state.texts.find(t => t.id === hitId);
      this.drag.startText = T ? { ...T } : null;

      this.setState({
        selectedId: hitId,
        guideV: null,
        guideH: null,
      }, () => {
        // single click starts editing (like your current behavior)
        this.beginEditing(hitId);
      });
    } else {
      // click empty: just select nothing for now (mouseUp will add text)
      this.setState({ selectedId: null, guideV: null, guideH: null }, this.markDirty);
    }

    // hide hint after first interaction
    if (this.state.showHint) {
      this.setState({ showHint: false });
    }
  };

  onPointerMoveCanvas = (e) => {
    const canvas = this.canvasRef.current;
    if (!canvas) return;

    const { x, y } = this.canvasToBaseFromClient(e.clientX, e.clientY);
    this.lastPointer = { x, y };

    // hover
    if (!this.drag.active) {
      const hit = this.hitText(x, y);
      if (hit !== this.state.hoverId) {
        this.setState({ hoverId: hit });
      }
      return;
    }

    // dragging threshold
    const dx0 = x - this.drag.startX;
    const dy0 = y - this.drag.startY;
    const dist = Math.hypot(dx0, dy0);

    if (!this.drag.started && dist >= 3) {
      // user intent: drag (stop typing)
      this.drag.started = true;
      this.stopCaret();
      this.setState({ typingActive: false, editingId: null }, this.markDirty);
    }

    if (!this.drag.started) return;
    if (!this.state.selectedId) return;

    // compute delta from last
    const dx = x - this.drag.lastX;
    const dy = y - this.drag.lastY;
    this.drag.lastX = x;
    this.drag.lastY = y;

    const shift = e.shiftKey;
    const alt = e.altKey;

    this.setState(s => {
      const id = s.selectedId;
      const { baseW, baseH } = s.renderMeta;
      const snapThresh = 10;

      let guideV = null;
      let guideH = null;

      const texts = s.texts.map(t => {
        if (t.id !== id) return t;

        let nx = t.x + dx;
        let ny = t.y + dy;

        // shift constrain
        if (shift) {
          if (Math.abs(dx0) > Math.abs(dy0)) ny = this.drag.startText ? this.drag.startText.y : ny;
          else nx = this.drag.startText ? this.drag.startText.x : nx;
        }

        // snapping (center lines) unless Alt is held
        if (!alt) {
          const cx = baseW / 2;
          const cy = baseH / 2;

          if (Math.abs(nx - cx) <= snapThresh) {
            nx = cx;
            guideV = cx;
          }
          if (Math.abs(ny - cy) <= snapThresh) {
            ny = cy;
            guideH = cy;
          }
        }

        return { ...t, x: nx, y: ny };
      });

      return { texts, guideV, guideH };
    }, this.markDirty);
  };

  onPointerUpCanvas = (e) => {
    this.drag.active = false;
    this.drag.pointerId = null;

    // if we dragged, commit & push history once
    if (this.drag.started) {
      this.pushHistory();
      this.drag.started = false;
      this.setState({ guideV: null, guideH: null }, this.markDirty);
      return;
    }

    // click (not drag): if empty, add new text
    const { x, y } = this.canvasToBaseFromClient(e.clientX, e.clientY);
    const hitId = this.hitText(x, y);

    if (!hitId) {
      this.pushHistory();

      const id = `T${Date.now()}`;
      const T = newText(id, x, y);

      this.setState(
        s => ({
          texts: [...s.texts, T],
          selectedId: id,
          editingId: id,
          typingActive: true,
          guideV: null,
          guideH: null,
        }),
        () => {
          this.beginEditing(id);
          this.markDirty();
        }
      );
    } else {
      // already handled by beginEditing in pointerDown
      this.markDirty();
    }
  };

  // Outside click: commit editing
  handleOutsidePointerDown = (e) => {
    const canvas = this.canvasRef.current;
    if (!canvas || !this.state.typingActive) return;

    const r = canvas.getBoundingClientRect();
    const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    if (!inside) this.commitEditing();
  };

  // -----------------------------
  // Keyboard shortcuts (undo/redo, delete, nudge, duplicate)
  // -----------------------------
  onKeyDownWindow = (e) => {
    // avoid interfering when typing in inputs
    const tag = (e.target?.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea') return;

    const isMac = navigator.platform.toLowerCase().includes('mac');
    const mod = isMac ? e.metaKey : e.ctrlKey;

    if (mod && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) this.redo();
      else this.undo();
      return;
    }
    if (mod && e.key.toLowerCase() === 'y') {
      e.preventDefault();
      this.redo();
      return;
    }

    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (this.state.selectedId && !this.state.typingActive) {
        e.preventDefault();
        this.deleteSelected();
      }
      return;
    }

    // arrows: nudge selected
    const step = e.shiftKey ? 10 : 1;
    if (this.state.selectedId && ['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const patch =
        e.key === 'ArrowLeft' ? { x: -step, y: 0 } :
        e.key === 'ArrowRight'? { x: step,  y: 0 } :
        e.key === 'ArrowUp'   ? { x: 0, y: -step } :
                                { x: 0, y: step };

      this.pushHistory();
      this.setState(s => ({
        texts: s.texts.map(t => t.id === s.selectedId ? { ...t, x: t.x + patch.x, y: t.y + patch.y } : t),
      }), this.markDirty);
      return;
    }

    // D: duplicate
    if (mod && e.key.toLowerCase() === 'd') {
      e.preventDefault();
      this.duplicateSelected();
      return;
    }
  };

  // -----------------------------
  // Selected text ops
  // -----------------------------
  updateSelected(patch, pushHistory = false) {
    const id = this.state.selectedId;
    if (!id) return;

    if (pushHistory) this.pushHistory();

    this.setState(s => ({
      texts: s.texts.map(t => t.id === id ? { ...t, ...patch } : t),
    }), this.markDirty);
  }

  deleteSelected = () => {
    const id = this.state.selectedId;
    if (!id) return;

    this.pushHistory();

    this.setState(s => ({
      texts: s.texts.filter(t => t.id !== id),
      selectedId: null,
      editingId: s.editingId === id ? null : s.editingId,
      typingActive: false,
      guideV: null,
      guideH: null,
    }), this.markDirty);
  };

  duplicateSelected = () => {
    const id = this.state.selectedId;
    if (!id) return;

    const src = this.state.texts.find(t => t.id === id);
    if (!src) return;

    this.pushHistory();

    const newId = `T${Date.now()}`;
    const copy = { ...src, id: newId, x: src.x + 20, y: src.y + 20 };

    this.setState(s => ({
      texts: [...s.texts, copy],
      selectedId: newId,
      editingId: null,
      typingActive: false,
    }), this.markDirty);
  };

  bringForward = () => {
    const id = this.state.selectedId;
    if (!id) return;

    const idx = this.state.texts.findIndex(t => t.id === id);
    if (idx < 0 || idx === this.state.texts.length - 1) return;

    this.pushHistory();

    this.setState(s => {
      const arr = [...s.texts];
      const [item] = arr.splice(idx, 1);
      arr.splice(idx + 1, 0, item);
      return { texts: arr };
    }, this.markDirty);
  };

  sendBackward = () => {
    const id = this.state.selectedId;
    if (!id) return;

    const idx = this.state.texts.findIndex(t => t.id === id);
    if (idx <= 0) return;

    this.pushHistory();

    this.setState(s => {
      const arr = [...s.texts];
      const [item] = arr.splice(idx, 1);
      arr.splice(idx - 1, 0, item);
      return { texts: arr };
    }, this.markDirty);
  };

  addTextAtPointer = () => {
    if (!this.state.imageSrc) return;

    this.pushHistory();

    const { x, y } = this.lastPointer;
    const id = `T${Date.now()}`;
    const T = newText(id, x, y);

    this.setState(
      s => ({
        texts: [...s.texts, T],
        selectedId: id,
        editingId: id,
        typingActive: true,
      }),
      () => {
        this.beginEditing(id);
        this.markDirty();
      }
    );
  };

  // -----------------------------
  // UI helpers (slider with value)
  // -----------------------------
  renderSliderRow(label, valueLabel, value, min, max, step, onChange, onCommit) {
    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="caption">{label}</Typography>
          <Typography variant="caption" color="text.secondary">{valueLabel}</Typography>
        </Stack>
        <Slider
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={onChange}
          onChangeCommitted={onCommit}
        />
      </Box>
    );
  }

  // -----------------------------
  // State changes that should render
  // -----------------------------
  setAspectRatio = (ratio) => {
    this.pushHistory();
    this.setState({ aspectRatio: ratio }, this.markDirty);
  };

  setFitMode = (mode) => {
    this.pushHistory();
    this.setState({ fitMode: mode }, this.markDirty);
  };

  setShowQrPreview = (v) => {
    this.pushHistory();
    this.setState({ showQrPreview: v }, this.markDirty);
  };

  // -----------------------------
  // Render
  // -----------------------------
  render() {
    const s = this.state;
    const selected = s.texts.find(t => t.id === s.selectedId) || null;
    const canUndo = this.history.length > 0;
    const canRedo = this.future.length > 0;

    return (
      <Box p={3} onPointerDown={this.handleOutsidePointerDown}>
        <Grid container spacing={2}>
          {/* Image input */}
          <Grid item xs={12}>
            <Dropzone onDrop={this.onDrop} accept={{ 'image/*': [] }}>
              {({ getRootProps, getInputProps, isDragActive }) => (
                <Box
                  {...getRootProps()}
                  p={3}
                  border="2px dashed #aaa"
                  textAlign="center"
                  bgcolor={isDragActive ? '#e0f7fa' : '#f9f9f9'}
                  sx={{ cursor: 'pointer' }}
                >
                  <input {...getInputProps()} />
                  <Typography variant="body2">
                    {isDragActive ? 'Drop the image here…' : 'Drag & drop an image here, or click to select one'}
                  </Typography>
                  {s.imageLoading && <Box mt={2}><LinearProgress /></Box>}
                  {s.imageSrc && !s.imageLoading && (
                    <Typography variant="body2" mt={1} color="text.secondary">Image loaded</Typography>
                  )}
                </Box>
              )}
            </Dropzone>
          </Grid>

          {/* Global settings */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Aspect Ratio</InputLabel>
              <Select
                value={s.aspectRatio}
                label="Aspect Ratio"
                onChange={(e) => this.setAspectRatio(Number(e.target.value))}
              >
                {ASPECTS.map(a => (
                  <MenuItem key={a.label} value={a.value}>{a.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Image Fit</InputLabel>
              <Select
                value={s.fitMode}
                label="Image Fit"
                onChange={(e) => this.setFitMode(e.target.value)}
              >
                {FIT_MODES.map(m => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4} alignSelf="center">
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
              <Switch
                checked={s.showQrPreview}
                onChange={(e) => this.setShowQrPreview(e.target.checked)}
              />
              <Typography variant="body2" color="text.secondary">
                show qr in preview (export excludes qr)
              </Typography>
            </Stack>
          </Grid>

          {/* Canvas + Toolbar */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Stack direction="row" spacing={1}>
                <Tooltip title="add text">
                  <span>
                    <IconButton onClick={this.addTextAtPointer} disabled={!s.imageSrc}>
                      <AddIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="duplicate (ctrl/cmd+d)">
                  <span>
                    <IconButton onClick={this.duplicateSelected} disabled={!selected}>
                      <ContentCopyIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="delete (del/backspace)">
                  <span>
                    <IconButton onClick={this.deleteSelected} disabled={!selected}>
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <Tooltip title="undo (ctrl/cmd+z)">
                  <span>
                    <IconButton onClick={this.undo} disabled={!canUndo}>
                      <UndoIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="redo (ctrl/cmd+shift+z)">
                  <span>
                    <IconButton onClick={this.redo} disabled={!canRedo}>
                      <RedoIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <Tooltip title="bring forward">
                  <span>
                    <IconButton onClick={this.bringForward} disabled={!selected}>
                      <ArrowUpwardIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="send backward">
                  <span>
                    <IconButton onClick={this.sendBackward} disabled={!selected}>
                      <ArrowDownwardIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                click = add • click text = edit • drag = move • shift = constrain • alt = disable snap
              </Typography>
            </Box>

            <Box
              mt={1}
              border="2px dashed #aaa"
              minHeight={300}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor="#f9f9f9"
              p={2}
              sx={{ position: 'relative' }}
            >
              {s.imageSrc ? (
                <>
                  <canvas
                    ref={this.canvasRef}
                    onPointerDown={this.onPointerDownCanvas}
                    onPointerMove={this.onPointerMoveCanvas}
                    onPointerUp={this.onPointerUpCanvas}
                    onPointerCancel={this.onPointerUpCanvas}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      cursor: this.getCanvasCursor(),
                      touchAction: 'none', // important for pointer drag on touch
                    }}
                  />

                  {s.showHint && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 12,
                        pointerEvents: 'none',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          borderRadius: 1,
                          bgcolor: 'rgba(0,0,0,0.5)',
                          color: '#fff',
                          maxWidth: 520,
                        }}
                      >
                        <Typography variant="body2">
                          tip: click to add text • drag to move • shift constrains • alt disables snap • enter finishes
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* hidden input to capture typing (keeps your original behavior) */}
                  <textarea
                    ref={this.hiddenInputRef}
                    onChange={this.onHiddenChange}
                    onKeyDown={this.onHiddenKeyDown}
                    onBlur={this.onHiddenBlur}
                    style={{ position: 'absolute', left: -9999, width: 0, height: 0, opacity: 0 }}
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  image preview will appear here
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Inspector */}
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1, bgcolor: '#fff' }}>
              <Typography variant="subtitle2" gutterBottom>selected text</Typography>

              {!selected && (
                <Typography variant="body2" color="text.secondary">
                  click the image to add text, then adjust styling here.
                </Typography>
              )}

              {selected && (
                <Grid container spacing={2}>
                  {/* Text content (visible editing) */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="text"
                      value={selected.text}
                      multiline
                      minRows={3}
                      onFocus={() => {
                        // mirror: focusing inspector starts editing too
                        if (this.state.selectedId) this.beginEditing(this.state.selectedId);
                      }}
                      onChange={(e) => {
                        // update live without pushing history each keystroke
                        const id = this.state.selectedId;
                        this.setState(s => ({
                          texts: s.texts.map(t => t.id === id ? { ...t, text: e.target.value } : t),
                        }), this.markDirty);
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      enter = finish • shift+enter = newline • esc = cancel
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Typography group */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>typography</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>font</InputLabel>
                      <Select
                        value={selected.fontFamily}
                        label="font"
                        onChange={(e) => this.updateSelected({ fontFamily: e.target.value }, true)}
                      >
                        {FONT_OPTIONS.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={8}>
                    {this.renderSliderRow(
                      'font size',
                      `${Math.round(selected.fontSize)}px`,
                      selected.fontSize,
                      10, 160, 1,
                      (_, v) => this.updateSelected({ fontSize: v }, false),
                      () => this.updateSelected({}, true) // push only
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="px"
                      type="number"
                      value={selected.fontSize}
                      onChange={(e) => this.updateSelected({ fontSize: Math.max(1, Number(e.target.value || 0)) }, false)}
                      onBlur={() => this.updateSelected({}, true)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    {this.renderSliderRow(
                      'line height',
                      `${selected.lineHeightMult.toFixed(2)}`,
                      selected.lineHeightMult,
                      1, 2, 0.05,
                      (_, v) => this.updateSelected({ lineHeightMult: v }, false),
                      () => this.updateSelected({}, true)
                    )}
                  </Grid>

                  <Grid item xs={6}>
                    {this.renderSliderRow(
                      'letter spacing',
                      `${selected.letterSpacing.toFixed(2)}px`,
                      selected.letterSpacing,
                      -1, 6, 0.25,
                      (_, v) => this.updateSelected({ letterSpacing: v }, false),
                      () => this.updateSelected({}, true)
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>align</Typography>
                    <ToggleButtonGroup
                      exclusive
                      value={selected.align}
                      onChange={(_, v) => v && this.updateSelected({ align: v }, true)}
                      fullWidth
                      size="small"
                    >
                      <ToggleButton value="left"><FormatAlignLeftIcon /></ToggleButton>
                      <ToggleButton value="center"><FormatAlignCenterIcon /></ToggleButton>
                      <ToggleButton value="right"><FormatAlignRightIcon /></ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>

                  <Grid item xs={12}>
                    {this.renderSliderRow(
                      'wrap width',
                      `${Math.round(selected.wrapWFrac * 100)}%`,
                      selected.wrapWFrac,
                      0.2, 1, 0.01,
                      (_, v) => this.updateSelected({ wrapWFrac: v }, false),
                      () => this.updateSelected({}, true)
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Color group */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>color</Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="text color"
                      type="color"
                      value={selected.color}
                      onChange={(e) => this.updateSelected({ color: e.target.value }, false)}
                      onBlur={() => this.updateSelected({}, true)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="outline color"
                      type="color"
                      value={selected.outlineColor}
                      onChange={(e) => this.updateSelected({ outlineColor: e.target.value }, false)}
                      onBlur={() => this.updateSelected({}, true)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={8}>
                    {this.renderSliderRow(
                      'outline width',
                      `${Math.round(selected.outlineWidth)}px`,
                      selected.outlineWidth,
                      0, 12, 1,
                      (_, v) => this.updateSelected({ outlineWidth: v }, false),
                      () => this.updateSelected({}, true)
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="px"
                      type="number"
                      value={selected.outlineWidth}
                      onChange={(e) => this.updateSelected({ outlineWidth: Math.max(0, Number(e.target.value || 0)) }, false)}
                      onBlur={() => this.updateSelected({}, true)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Backdrop group */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>backdrop</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Switch
                        checked={selected.backdrop}
                        onChange={(e) => this.updateSelected({ backdrop: e.target.checked }, true)}
                      />
                      <Typography variant="body2">text backdrop</Typography>
                    </Stack>
                  </Grid>

                  {selected.backdrop && (
                    <>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="backdrop color"
                          type="color"
                          value={selected.backdropColor}
                          onChange={(e) => this.updateSelected({ backdropColor: e.target.value }, false)}
                          onBlur={() => this.updateSelected({}, true)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        {this.renderSliderRow(
                          'opacity',
                          `${Math.round(selected.backdropOpacity * 100)}%`,
                          selected.backdropOpacity,
                          0, 1, 0.05,
                          (_, v) => this.updateSelected({ backdropOpacity: v }, false),
                          () => this.updateSelected({}, true)
                        )}
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Danger zone */}
                  <Grid item xs={12}>
                    <Box
                      onClick={this.deleteSelected}
                      sx={{
                        px: 2,
                        py: 1,
                        border: '1px solid #ddd',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { background: '#f8f8f8' }
                      }}
                    >
                      <Typography variant="body2" color="error.main">delete text</Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Make sure canvas updates when state changes
  componentDidUpdate(prevProps, prevState) {
    const keys = [
      'imageSrc','aspectRatio','fitMode','texts','selectedId','editingId',
      'typingActive','caretOn','showQrPreview','guideV','guideH'
    ];

    for (const k of keys) {
      if (prevState[k] !== this.state[k]) {
        this.markDirty();
        break;
      }
    }

    if (prevState.typingActive !== this.state.typingActive) {
      if (this.state.typingActive) this.startCaret();
      else this.stopCaret();
    }
  }
}
